/** @file Sets the setup draw order: a full slot permutation, or one slot moved above/below another. */
import { z } from 'zod';
import { isSlotPermutation } from '@pose2d/format/doc/isSlotPermutation';
import { throwError } from '@pose2d/format/throwError';
import type { Doc, Id } from '@pose2d/format/types';
import { defineCommand } from '../defineCommand';
import type { CommandContext } from '../commandContext';

const slotRef = z.string().min(1);
type Move = { target: string; above: string } | { target: string; below: string };

/** The resolved full order; VALIDATION at `order` unless it names every slot exactly once. */
function fullOrder(doc: Doc, refs: readonly string[], ctx: CommandContext): Id[] {
  const order = refs.map((ref) => ctx.resolve('slot', ref));
  if (!isSlotPermutation(doc, order)) {
    throwError('VALIDATION', undefined, { path: 'order', hint: `list every slot exactly once (${Object.keys(doc.slots).length} slots; drawOrder[0] is drawn first, at the back)` });
  }
  return order;
}

/** `order` with `target` moved directly after (above) or before (below) the reference slot. */
function moved(order: readonly Id[], move: Move, ctx: CommandContext): Id[] {
  const above = 'above' in move;
  const path = above ? 'above' : 'below';
  const target = ctx.resolve('slot', move.target);
  const ref = ctx.resolve('slot', 'above' in move ? move.above : move.below);
  if (ref === target) throwError('VALIDATION', undefined, { path, hint: 'a slot cannot be moved relative to itself' });
  const place = (id: Id): Id[] => (above ? [id, target] : [target, id]);
  return order.filter((id) => id !== target).flatMap((id) => (id === ref ? place(id) : [id]));
}

/** slot.setDrawOrder: assigns a whole new array (no splice); the current order is a no-op (W2-31). */
export const command = defineCommand({
  name: 'slot.setDrawOrder',
  description: 'Set the setup draw order (drawOrder[0] is drawn first, at the back): { order: every slot once } or move one slot with { target, above: slot } (drawn right after it) or { target, below: slot } (drawn right before it).',
  input: z.union([
    z.object({ order: z.array(slotRef).min(1) }).strict(),
    z.object({ target: slotRef, above: slotRef }).strict(),
    z.object({ target: slotRef, below: slotRef }).strict(),
  ]),
  apply(draft, input, ctx) {
    const next = 'order' in input ? fullOrder(draft, input.order, ctx) : moved(draft.drawOrder, input, ctx);
    if (next.every((id, i) => id === draft.drawOrder[i])) return;
    draft.drawOrder = next;
    draft.meta.updatedAt = ctx.now;
  },
});
